<script setup lang="ts">
import { computed } from 'vue';
import type { ConnectOptionKind } from '@/common/bot/enum';
import { useMessageActionsCustomisation } from '../../composables/useMessageActionsCustomisation';

export interface ConnectOptionProps {
  type: ConnectOptionKind;
  title: string;
  estimate?: string;
  selected?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<ConnectOptionProps>(), {
  disabled: false,
});

const emit = defineEmits<{
  (e: 'select', type: ConnectOptionKind): void;
}>();

// Apply customisation
const customisation = useMessageActionsCustomisation();

const buttonStyle = computed(() => {
  const textColor = customisation.suggestionsTextColor.value;
  const backgroundColor = customisation.suggestionsBackgroundColor.value;
  const borderColor = customisation.suggestionsBorderColor.value;
  const selectedColor = customisation.suggestionsSelectedBorderColor.value;

  return {
    ...(textColor ? { color: textColor } : {}),
    ...(backgroundColor ? { backgroundColor } : {}),
    ...(props.selected && selectedColor ? { borderColor: selectedColor } : borderColor ? { borderColor } : {}),
  };
});

const handleClick = (type: ConnectOptionKind) => {
  if (props.disabled) return;
  emit('select', type);
};
</script>

<template>
  <button
    type="button"
    class="btn-radius connect-option"
    :class="{ 'connect-option--selected': selected }"
    :style="buttonStyle"
    :disabled="disabled"
    :data-testid="`connect-option-${type}`"
    @click="handleClick(type)"
  >
    <div class="connect-option__content">
      <span class="connect-option__title theme-font-main">{{ title }}</span>
      <span v-if="estimate" class="connect-option__estimate theme-font-subtext">{{ estimate }}</span>
    </div>
  </button>
</template>

<style scoped>
.connect-option {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  text-align: left;
}

.connect-option:hover {
  opacity: 0.8;
}

.connect-option:active:not(:disabled) {
  transform: scale(0.98);
}

.connect-option:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.connect-option__content {
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 4px;
  width: 100%;
}
</style>
